import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/management/brands/trade-license/mappings';

export interface IMappingDetailsById {
  id: number;
  entityName: string;
  tradeLicenseName: string;
  licenseNumber: string;
  licenseConfidence: number;
  brandId: number | null;
  brandConfidence: number;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  brand: {
    id: number;
    name: string;
  } | null;
  dedDetail: {
    id: number;
    licenseNo: string;
    englishName: string;
    businessName: string;
    legalType: string;
  } | null;
}

type TQueryKey = [typeof endpoint, number];
type Options = UseQueryOptions<IMappingDetailsById, AxiosError, IMappingDetailsById, TQueryKey>;
type ReturnType = UseQueryResult<IMappingDetailsById>;

export function useGetMappingByIdApi(mappingId: number, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async (): Promise<IMappingDetailsById> => {
    try {
      const { data } = await http.get(`${endpoint}/${mappingId}`);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        title: 'Error Loading Mapping Details',
        description: error.response.data.message || 'Failed to load mapping details. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, mappingId], queryFn, {
    enabled: isLoggedIn && !!mappingId,
    ...options,
  });
}
